import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { Plus, Search, Trash2, Star } from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import EventPagination from '@/components/EventPagination'
import type { PaginationMeta } from '@/types'

type AdminBlogPostRow = {
  id: string
  title: string
  slug: string
  status: 'draft' | 'published'
  featured: boolean
  category: { id: string; name: string } | null
  updated_at: string
  published_at: string | null
  cover_image_url: string | null
  updated_by: string | null
}

type Props = {
  posts: AdminBlogPostRow[]
  pagination: PaginationMeta
  filters: { status?: string; q?: string }
}

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'published', label: 'Published' },
]

const STATUS_BADGES: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  draft: { label: 'Draft', variant: 'outline' },
  published: { label: 'Published', variant: 'default' },
}

export default function AdminBlogPostsIndex({ posts, pagination, filters }: Props) {
  const [query, setQuery] = useState(filters.q || '')

  function applyFilter(next: { status?: string; q?: string }) {
    const merged = { ...filters, ...next }
    const params = Object.fromEntries(
      Object.entries(merged).filter(([, v]) => v && v !== ''),
    )
    router.get('/admin/blog_posts', params, { preserveState: false })
  }

  function search(e: React.FormEvent) {
    e.preventDefault()
    applyFilter({ q: query.trim() })
  }

  function destroy(post: AdminBlogPostRow) {
    if (!confirm(`Delete "${post.title}"? This cannot be undone.`)) return
    router.delete(`/admin/blog_posts/${post.id}`, { preserveState: false })
  }

  return (
    <>
      <Head title="Admin · Blog" />
      <div>
        <div className="flex items-center justify-between gap-4">
          <h1 className="font-serif text-3xl font-semibold tracking-tight">Blog</h1>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link href="/admin/blog_categories">Categories</Link>
            </Button>
            <Button asChild>
              <Link href="/admin/blog_posts/new">
                <Plus className="size-4" />
                New post
              </Link>
            </Button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => {
              const active = (filters.status || '') === f.value
              return (
                <Button
                  key={f.value || 'all'}
                  type="button"
                  variant={active ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyFilter({ status: f.value })}
                >
                  {f.label}
                </Button>
              )
            })}
          </div>
          <form onSubmit={search} className="relative w-72">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search by title"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </form>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                    No posts match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                posts.map((post) => {
                  const badge = STATUS_BADGES[post.status]
                  return (
                    <TableRow key={post.id}>
                      <TableCell>
                        <Link
                          href={`/admin/blog_posts/${post.id}/edit`}
                          className="flex items-center gap-3 font-medium hover:underline"
                        >
                          {post.cover_image_url ? (
                            <img
                              src={post.cover_image_url}
                              alt=""
                              className="h-10 w-16 rounded-md object-cover ring-1 ring-black/5"
                            />
                          ) : (
                            <div className="h-10 w-16 rounded-md bg-muted/50 ring-1 ring-black/5" />
                          )}
                          <span className="flex items-center gap-1">
                            {post.title}
                            {post.featured && <Star className="size-3 fill-primary text-primary" />}
                          </span>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {post.category?.name || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(post.updated_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                        {post.updated_by && (
                          <span className="block text-xs text-muted-foreground/80">
                            by {post.updated_by}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => destroy(post)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>

        <EventPagination
          pagination={pagination}
          baseUrl="/admin/blog_posts"
          filters={{ status: filters.status, q: filters.q }}
        />
      </div>
    </>
  )
}

AdminBlogPostsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
